import { z } from 'zod';

export const chartSessionGlobalContextSchema = z
  .object({
    documents: z.array(z.string()).optional().nullable(),
    folders: z.array(z.string()).optional().nullable(),
    articles: z.array(z.coerce.number()).optional().nullable(),
    include_library: z.boolean().optional().nullable(),
  })
  .transform((raw) => ({
    documents: raw.documents ?? [],
    folders: raw.folders ?? [],
    articles: raw.articles ?? [],
    include_library: raw.include_library ?? false,
  }));

export const ChartSessionItemSchema = z
  .object({
    title: z.string().nullable().optional(),
    author_id: z.string(),
    id: z.uuid(),
    organization_id: z.number().int().nonnegative(),
    created_at: z.iso.datetime(),
    updated_at: z.iso.datetime(),
    global_context: chartSessionGlobalContextSchema.optional().nullable(),
  })
  .transform((raw) => ({
    title: raw.title,
    authorId: raw.author_id,
    id: raw.id,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
    globalContext: raw.global_context ?? { documents: [], folders: [], articles: [] },
  }));

export const chartSessionSchema = z.object({
  items: z.array(ChartSessionItemSchema),
  total: z.number().int().nonnegative(),
});

export const ChartSessionMessageContextSchema = z
  .object({
    documents: z.array(z.string()).optional().nullable(),
    folders: z.array(z.string()).optional().nullable(),
    articles: z.array(z.coerce.number()).optional().nullable(),
  })
  .transform((raw) => ({
    documents: raw.documents ?? [],
    folders: raw.folders ?? [],
    articles: raw.articles ?? [],
  }));

export const ChartSessionMessageSchema = z
  .object({
    text: z.string(),
    context: ChartSessionMessageContextSchema.nullish(),
    llm_model: z.string().nullable().optional(),
    id: z.string(),
    role: z.string(),
    session_id: z.string(),
    author_id: z.string().nullable().optional(),
    organization_id: z.number().int().nonnegative().nullable().optional(),
    rating: z.number().int().nullable().optional(),
    created_at: z.string(),
    updated_at: z.string(),
  })
  .transform((raw) => ({
    text: raw.text,
    context: raw.context ? raw.context : { documents: [], folders: [] },
    llmModel: raw.llm_model ?? null,
    id: raw.id,
    role: raw.role,
    sessionId: raw.session_id,
    authorId: raw.author_id ?? null,
    organizationId: raw.organization_id ?? null,
    rating: raw.rating ?? null,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
  }));

export const chartSessionMessagesPaginatorSchema = z.object({
  total_count: z.number().int().nonnegative(),
  page_size: z.number().int().nonnegative(),
  page: z.number().int().nonnegative(),
});

export const chartSessionMessagesSchema = z.object({
  items: z.array(ChartSessionMessageSchema),
  total: z.number().int().nonnegative(),
  paginator: chartSessionMessagesPaginatorSchema.optional(),
});

export type ChartSession = z.output<typeof chartSessionSchema>;
export type ChartSessionApi = z.input<typeof chartSessionSchema>;
export type ChartSessionItem = z.output<typeof ChartSessionItemSchema>;
export type ChartSessionItemApi = z.input<typeof ChartSessionItemSchema>;
export type ChartSessionGlobalContext = z.output<typeof chartSessionGlobalContextSchema>;
export type ChartSessionGlobalContextApi = z.input<typeof chartSessionGlobalContextSchema>;
export type ChartSessionMessageContext = z.output<typeof ChartSessionMessageContextSchema>;
export type ChartSessionMessageContextApi = z.input<typeof ChartSessionMessageContextSchema>;
export type ChartSessionMessage = z.output<typeof ChartSessionMessageSchema>;
export type ChartSessionMessageApi = z.input<typeof ChartSessionMessageSchema>;
export type ChartSessionMessagesPaginator = z.output<typeof chartSessionMessagesPaginatorSchema>;
export type ChartSessionMessagesPaginatorApi = z.input<typeof chartSessionMessagesPaginatorSchema>;
export type ChartSessionMessages = z.output<typeof chartSessionMessagesSchema>;
export type ChartSessionMessagesApi = z.input<typeof chartSessionMessagesSchema>;
